// ImageUploadModal.jsx
import React, { useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { FaUpload, FaTimesCircle } from "react-icons/fa";
import { uploadImage } from "../services/ImageService";
import "./ImageUploadModal.css";
import "../containers/ModalTheme.css";

export default function ImageUploadModal({ show, onHide, onUploadSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a PNG, JPEG, or WebP image');
      return;
    }

    // Validate file size (8MB max)
    if (file.size > 8 * 1024 * 1024) {
      setError('File size must be less than 8MB');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    try {
      const data = await uploadImage(selectedFile);

      if (data.status && data.payload) {
        // Clear state
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Call success callback
        onUploadSuccess(data.payload);
        onHide();
      } else {
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      fileInputRef.current.files = files;
      handleFileSelect({ target: { files } });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered contentClassName="as-theme as-modal">
      <Modal.Header>
        <div className="as-modal-header-row">
          <div className="as-modal-title-row">
            <span className="as-modal-icon-badge" aria-hidden="true">📤</span>
            <Modal.Title>Upload Image</Modal.Title>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
            aria-label="Close"
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div 
          className="upload-dropzone"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {previewUrl && selectedFile ? (
            <div className="upload-preview">
              <img src={previewUrl} alt="Preview" />
              <div className="upload-preview-info">
                <span>{selectedFile.name}</span>
                <span className="text-muted">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </span>
              </div>
            </div>
          ) : (
            <div className="upload-placeholder">
              <FaUpload className="upload-placeholder-icon" />
              <p>Click or drag and drop</p>
              <span className="text-muted">PNG, JPEG, or WebP (max 8MB)</span>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {error && (
          <div className="upload-error">
            <FaTimesCircle className="me-2" />
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="as-modal-footer">
        <button className="btn btn-sm as-btn-soft" onClick={handleClose} disabled={uploading}>
          Cancel
        </button>
        <button 
          className="btn btn-sm as-btn-primary-gradient" 
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
        >
          {uploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Uploading...
            </>
          ) : (
            <>
              <FaUpload className="me-2" />
              Upload
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
